/**
 * @fileoverview Phase 1C complex-visit planning contracts and pure visit rules
 *               — operation intents for `create-complex-visit` /
 *               `create-complex-reinspection` plus the deterministic visit
 *               index/finalization/progress/reuse rules every host shares
 *               (document 13 §10–§12, document 16 §3–§5).
 * @module import-core/contracts/complex-inspections/complexVisitPlanning
 *
 * Responsibilities:
 * - Define the strict intent schemas the complex-mutation boundary parses for
 *   initial complex visits and complex reinspections.
 * - Define the pure rules: visit index projections, auto-reinspection
 *   deferral, and never-started initial case compatibility. Root
 *   finalization + visit progress live in `./complexVisitFinalization`
 *   (which consumes `./caseSchemas` — kept apart so this module stays a
 *   caseSchemas dependency, never the reverse).
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas` only (browser-safe — no mongodb/node runtime
 *   imports and deliberately NO import from sibling mutation/case modules so
 *   caseSchemas can consume the rules without an import cycle).
 *
 * Architecture:
 * - Pure schemas + deterministic pure functions, no I/O. The tenant-api
 *   `complexVisitMutationExecutor` plans writes from these rules inside the
 *   Phase 1B receipt/transaction boundary; tenant-web reads progress and
 *   eligibility from the same rules over Dexie documents.
 *
 * Notes:
 * - The events ARRAY index is authoritative everywhere — an event's own
 *   `eventIndex` property is untrusted data and is never used for reference
 *   resolution.
 * - Terminal event statuses are exactly `completed` and `closedOut`.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  isoDateLikeSchema,
  lookupReferenceSchema,
} from './sharedSchemas';

// ---------------------------------------------------------------------------
// Intent schemas (document 16 §6 — patch payloads, never raw Mongo)
// ---------------------------------------------------------------------------

/**
 * @contract complexVisitAssignmentSchema + ComplexVisitAssignment
 * Purpose: Inspector assignment for one visit participant event. Carries the
 *   primary inspector plus optional secondary inspectors.
 * Fields: primaryInspectorId, primaryInspectorName, secondaryInspectorIds?,
 *   secondaryInspectorNames?.
 * Rules: secondary ids/names appear TOGETHER or not at all, with equal
 *   length — every secondary id pairs with its display name; secondary ids
 *   are unique and never repeat the primary inspector.
 * Sync: embedded in the create-complex-visit/reinspection intent patches and
 *   applied verbatim onto the created/appended events.
 */
export const complexVisitAssignmentSchema = z
  .object({
    primaryInspectorId: objectIdLikeSchema,
    primaryInspectorName: z.string().min(1),
    secondaryInspectorIds: z.array(objectIdLikeSchema).optional(),
    secondaryInspectorNames: z.array(z.string()).optional(),
  })
  .strict()
  .superRefine((assignment, ctx) => {
    const ids = assignment.secondaryInspectorIds;
    const names = assignment.secondaryInspectorNames;
    if ((ids === undefined) !== (names === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [ids === undefined ? 'secondaryInspectorIds' : 'secondaryInspectorNames'],
        message: 'secondaryInspectorIds and secondaryInspectorNames must appear together',
      });
    }
    if (ids !== undefined && names !== undefined && ids.length !== names.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['secondaryInspectorNames'],
        message: 'secondaryInspectorIds and secondaryInspectorNames must have equal length',
      });
    }
    if (ids !== undefined) {
      const canon = (value: unknown) => {
        const raw = String(value);
        return /^[a-f\d]{24}$/i.test(raw) ? raw.toLowerCase() : raw;
      };
      const primaryKey = canon(assignment.primaryInspectorId);
      const seen = new Set<string>();
      ids.forEach((id, index) => {
        const key = canon(id);
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['secondaryInspectorIds', index],
            message: 'secondaryInspectorIds must be unique',
          });
        }
        seen.add(key);
        if (key === primaryKey) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['secondaryInspectorIds', index],
            message: 'secondaryInspectorIds must not repeat primaryInspectorId',
          });
        }
      });
    }
  });
export type ComplexVisitAssignment = z.infer<typeof complexVisitAssignmentSchema>;

/**
 * @contract complexInitialVisitParticipantIntentSchema +
 *   ComplexInitialVisitParticipantIntent
 * Purpose: One entity's plan inside a `create-complex-visit` intent —
 *   everything the server needs to reuse-or-create exactly one case with
 *   exactly one initial scheduled inspection event.
 * Fields: entityId, proposedCaseId (client-minted deterministic id used only
 *   when no compatible case exists), inspectionAccountId,
 *   inspectionAccountName?, templateId, caseType, inspectionType, eventType,
 *   fiscalYear, assignment, scheduledDate?/scheduledStart?/scheduledEnd?,
 *   internalNotes?.
 * Rules: scheduledEnd requires scheduledStart and cannot precede it; all
 *   LookupReferences must resolve server-side against real tenant lookup
 *   rows (id + type + code).
 * Sync: parsed from the operation `patch` — travels in the request and the
 *   local `complex_mutations` outbox.
 */
export const complexInitialVisitParticipantIntentSchema = z
  .object({
    entityId: objectIdLikeSchema,
    proposedCaseId: objectIdLikeSchema,
    inspectionAccountId: objectIdLikeSchema,
    inspectionAccountName: z.string().optional(),
    templateId: objectIdLikeSchema,
    caseType: lookupReferenceSchema,
    inspectionType: lookupReferenceSchema,
    eventType: lookupReferenceSchema,
    fiscalYear: z.number().int(),
    assignment: complexVisitAssignmentSchema,
    scheduledDate: isoDateLikeSchema.optional(),
    scheduledStart: isoDateLikeSchema.optional(),
    scheduledEnd: isoDateLikeSchema.optional(),
    internalNotes: z.string().optional(),
  })
  .strict()
  .superRefine((participant, ctx) => {
    if (participant.scheduledEnd !== undefined && participant.scheduledStart === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledEnd'],
        message: 'scheduledEnd requires scheduledStart',
      });
    }
    if (participant.scheduledEnd !== undefined && participant.scheduledStart !== undefined) {
      if (new Date(participant.scheduledEnd).getTime() < new Date(participant.scheduledStart).getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scheduledEnd'],
          message: 'scheduledEnd cannot precede scheduledStart',
        });
      }
    }
  });
export type ComplexInitialVisitParticipantIntent = z.infer<
  typeof complexInitialVisitParticipantIntentSchema
>;

/**
 * @contract createComplexVisitIntentSchema + CreateComplexVisitIntent
 * Purpose: `create-complex-visit` patch — the full participant plan for one
 *   initial complex field visit (root = group anchor, participants = the
 *   remaining selected members).
 * Fields: visitId (client-minted stable id), participants[] (min 1).
 * Rules: participants are unique by entityId AND by proposedCaseId; every
 *   participant shares one inspectionAccountId (accepted initial release —
 *   one account per complex visit).
 * Sync: parsed from the single `inspectableEntityGroups` change patch.
 */
export const createComplexVisitIntentSchema = z
  .object({
    visitId: objectIdLikeSchema,
    participants: z.array(complexInitialVisitParticipantIntentSchema).min(1),
  })
  .strict()
  .superRefine((intent, ctx) => {
    const entityIds = new Set<string>();
    const caseIds = new Set<string>();
    const accountIds = new Set<string>();
    intent.participants.forEach((participant, index) => {
      const entityKey = String(participant.entityId);
      if (entityIds.has(entityKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participants', index, 'entityId'],
          message: 'participants must be unique by entityId',
        });
      }
      entityIds.add(entityKey);
      const caseKey = String(participant.proposedCaseId);
      if (caseIds.has(caseKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participants', index, 'proposedCaseId'],
          message: 'participants must be unique by proposedCaseId',
        });
      }
      caseIds.add(caseKey);
      accountIds.add(String(participant.inspectionAccountId));
    });
    if (accountIds.size > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['participants'],
        message: 'all participants must share one inspectionAccountId (one account per complex visit)',
      });
    }
  });
export type CreateComplexVisitIntent = z.infer<typeof createComplexVisitIntentSchema>;

/**
 * @contract complexReinspectionParticipantIntentSchema +
 *   ComplexReinspectionParticipantIntent
 * Purpose: One existing governed case included in a `create-complex-
 *   reinspection` intent — appends exactly one new reinspection event.
 * Fields: caseId, assignmentOverride?.
 * Rules: caseId must name an existing nondeleted governed participant case;
 *   the root case is never listed here.
 * Sync: parsed from the operation patch.
 */
export const complexReinspectionParticipantIntentSchema = z
  .object({
    caseId: objectIdLikeSchema,
    assignmentOverride: complexVisitAssignmentSchema.optional(),
  })
  .strict();
export type ComplexReinspectionParticipantIntent = z.infer<
  typeof complexReinspectionParticipantIntentSchema
>;

/**
 * @contract createComplexReinspectionIntentSchema + CreateComplexReinspectionIntent
 * Purpose: `create-complex-reinspection` patch — a new visitId + new root
 *   manifest covering exactly the listed eligible governed cases.
 * Fields: visitId, rootCaseId, participants[] (min 1, EXCLUDES the root),
 *   eventType (the actual reinspection event type), inspectionType,
 *   defaultAssignment, scheduledDate?/scheduledStart?/scheduledEnd?,
 *   internalNotes?.
 * Rules: participant caseIds unique; no participant may equal rootCaseId;
 *   scheduledEnd requires scheduledStart and cannot precede it. Prior
 *   manifests are never mutated.
 * Sync: parsed from the single `inspectableEntityGroups` change patch.
 */
export const createComplexReinspectionIntentSchema = z
  .object({
    visitId: objectIdLikeSchema,
    rootCaseId: objectIdLikeSchema,
    participants: z.array(complexReinspectionParticipantIntentSchema).min(1),
    eventType: lookupReferenceSchema,
    inspectionType: lookupReferenceSchema,
    defaultAssignment: complexVisitAssignmentSchema,
    scheduledDate: isoDateLikeSchema.optional(),
    scheduledStart: isoDateLikeSchema.optional(),
    scheduledEnd: isoDateLikeSchema.optional(),
    internalNotes: z.string().optional(),
  })
  .strict()
  .superRefine((intent, ctx) => {
    const caseIds = new Set<string>();
    intent.participants.forEach((participant, index) => {
      const key = String(participant.caseId);
      if (caseIds.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participants', index, 'caseId'],
          message: 'participants must be unique by caseId',
        });
      }
      caseIds.add(key);
      if (key === String(intent.rootCaseId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['participants', index, 'caseId'],
          message: 'the root case is never a reinspection participant',
        });
      }
    });
    if (intent.scheduledEnd !== undefined && intent.scheduledStart === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['scheduledEnd'],
        message: 'scheduledEnd requires scheduledStart',
      });
    }
    if (intent.scheduledEnd !== undefined && intent.scheduledStart !== undefined) {
      if (new Date(intent.scheduledEnd).getTime() < new Date(intent.scheduledStart).getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scheduledEnd'],
          message: 'scheduledEnd cannot precede scheduledStart',
        });
      }
    }
  });
export type CreateComplexReinspectionIntent = z.infer<typeof createComplexReinspectionIntentSchema>;

// ---------------------------------------------------------------------------
// Internal id helper — mirrors toCanonicalIdString (kept local so this module
// never imports complexMutationSchemas: caseSchemas consumes these rules and
// complexMutationSchemas consumes these intent schemas).
// ---------------------------------------------------------------------------

const OBJECT_ID_HEX_PLANNING = /^[a-f\d]{24}$/i;

function idKey(value: unknown): string | null {
  if (typeof value === 'string' && OBJECT_ID_HEX_PLANNING.test(value)) return value.toLowerCase();
  if (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { toHexString?: unknown }).toHexString === 'function'
  ) {
    const hex = (value as { toHexString: () => string }).toHexString();
    if (OBJECT_ID_HEX_PLANNING.test(hex)) return hex.toLowerCase();
  }
  return null;
}

function readEvents(doc: unknown): Record<string, any>[] {
  const events = (doc as Record<string, any> | null | undefined)?.events;
  return Array.isArray(events) ? (events as Record<string, any>[]) : [];
}

// ---------------------------------------------------------------------------
// Pure visit rules
// ---------------------------------------------------------------------------

/**
 * @contract ComplexVisitIndexProjection
 * Purpose: The exact materialized query projections a target case must carry
 *   — deduplicated in EVENT ORDER, derived from event visit objects only.
 * Fields: complexVisitIds, complexGroupIds, complexRootVisitIds (string hex
 *   or ObjectId-like values as stored).
 * Rules: root ids come ONLY from manifests (never participant refs); ref
 *   visit/group ids land in complexVisitIds/complexGroupIds.
 * Sync: stored on the case; compared exactly by inspectionCaseDocumentSchema.
 */
export interface ComplexVisitIndexProjection {
  complexVisitIds: unknown[];
  complexGroupIds: unknown[];
  complexRootVisitIds: unknown[];
}

/**
 * @process deriveComplexVisitIndexProjection
 * Steps: walk events in array order; a manifest contributes visitId →
 *   visitIds + rootVisitIds and groupId → groupIds; a participant reference
 *   contributes visitId → visitIds and groupId → groupIds; dedupe by
 *   canonical id string preserving first-seen order.
 * Inputs: events — the case events array (raw documents).
 * Outputs: ComplexVisitIndexProjection.
 * Failure Handling: malformed id values are skipped (schema validation is
 *   the authority on malformed visit objects).
 * Idempotency: REQUIRED and satisfied — pure derivation.
 */
export function deriveComplexVisitIndexProjection(events: unknown[]): ComplexVisitIndexProjection {
  const visitIds: unknown[] = [];
  const groupIds: unknown[] = [];
  const rootVisitIds: unknown[] = [];
  const seenVisit = new Set<string>();
  const seenGroup = new Set<string>();
  const seenRoot = new Set<string>();
  const push = (list: unknown[], seen: Set<string>, value: unknown) => {
    const key = idKey(value);
    if (key === null || seen.has(key)) return;
    seen.add(key);
    list.push(value);
  };
  for (const event of events) {
    const record = event as Record<string, any> | null | undefined;
    const manifest = record?.complexVisitManifest as Record<string, any> | undefined;
    const reference = record?.complexVisitReference as Record<string, any> | undefined;
    if (manifest) {
      push(visitIds, seenVisit, manifest.visitId);
      push(groupIds, seenGroup, manifest.groupId);
      push(rootVisitIds, seenRoot, manifest.visitId);
    }
    if (reference) {
      push(visitIds, seenVisit, reference.visitId);
      push(groupIds, seenGroup, reference.groupId);
    }
  }
  return { complexVisitIds: visitIds, complexGroupIds: groupIds, complexRootVisitIds: rootVisitIds };
}

/**
 * @process shouldDeferAutoReinspectionForComplexEvent
 * Steps: an event carrying a root `complexVisitManifest` defers; an event
 *   carrying a `complexVisitReference` defers iff its participationMode is
 *   'complex-governed'; standalone references and plain events return false.
 * Inputs: event — the completing case event (raw document).
 * Outputs: boolean — true means the single-case auto-reinspection path must
 *   NOT fire; complex scheduling owns the next visit.
 * Failure Handling: malformed/missing objects → false (non-deferral is the
 *   legacy default).
 * Idempotency: REQUIRED and satisfied — pure read.
 */
export function shouldDeferAutoReinspectionForComplexEvent(event: unknown): boolean {
  const record = event as Record<string, any> | null | undefined;
  if (!record || typeof record !== 'object') return false;
  if (record.complexVisitManifest !== undefined && record.complexVisitManifest !== null) return true;
  const reference = record.complexVisitReference as Record<string, any> | undefined;
  if (reference && typeof reference === 'object') {
    return reference.participationMode === 'complex-governed';
  }
  return false;
}

// ---------------------------------------------------------------------------
// Never-started initial case reuse (document 13 §10)
// ---------------------------------------------------------------------------

/**
 * @contract CompatibleInitialCaseResult
 * Purpose: Discriminated outcome of the initial-case reuse rule —
 *   `reuse` carries the exact case/event to attach the participant
 *   reference to; `blocked` carries the reason a matching case exists but
 *   cannot receive a new initial visit; `none` means create a new case.
 * Rules: `blocked` reasons: `INITIAL_EVENT_ALREADY_STARTED` (a matching
 *   same-program initial event is inProgress/completed/closedOut, was
 *   started, or already carries a visit object), or `INCOMPATIBLE_CASE`
 *   (an active same-program case holds a scheduled untouched initial whose
 *   template id/current version does not match — the executor rejects
 *   rather than create a second initial). Archived/caseCompleted/closedOut
 *   prior cycles are ignored entirely — a new case is allowed.
 * Sync: pure decision artifact consumed by the visit executor.
 */
export type CompatibleInitialCaseResult =
  | { kind: 'reuse'; caseId: unknown; eventIndex: number }
  | { kind: 'blocked'; reason: 'INITIAL_EVENT_ALREADY_STARTED' | 'INCOMPATIBLE_CASE'; caseId: unknown }
  | { kind: 'none' };

/** Case statuses that represent finished prior cycles — never block/reuse. */
const PRIOR_CYCLE_CASE_STATUSES = new Set(['closedOut', 'caseCompleted']);

function eventIsMatchingInitial(event: Record<string, any>, intent: ComplexInitialVisitParticipantIntent): boolean {
  if (event?.eventCategory !== 'inspection') return false;
  // Reinspection events are never initial candidates.
  if (typeof event.reinspectionNumber === 'number' && event.reinspectionNumber > 0) return false;
  const inspectionType = event.inspectionType as Record<string, any> | undefined;
  // Exact event inspectionType id is required — a missing reference is not
  // compatible and cannot establish the program tuple.
  if (!inspectionType || idKey(inspectionType.lookupId) === null) return false;
  return idKey(inspectionType.lookupId) === idKey(intent.inspectionType.lookupId);
}

/**
 * @process findCompatibleNeverStartedInitialCase
 * Steps: the active program tuple is entity + caseType (lookupId) +
 *   inspectionAccountId + fiscalYear + inspectionType — a case/event outside
 *   that tuple NEVER blocks or is reused. Prior-cycle cases
 *   (`caseStatus` closedOut/caseCompleted, `_deleted`, or `archivedAt`) are
 *   ignored entirely. Within an active same-program case an initial event is
 *   reusable iff it is `scheduled`, never started/completed/closed (no
 *   startedAt/completedAt/closedOutAt), and carries no visit object AND its
 *   case's templateId/current template version match. A matching initial
 *   that has started/completed/closed or already carries a visit object
 *   blocks with `INITIAL_EVENT_ALREADY_STARTED`; an active same-program case
 *   whose untouched scheduled initial uses a different template id/version
 *   blocks with `INCOMPATIBLE_CASE` (no duplicate initial is created).
 * Inputs: cases — candidate case documents; participantIntent — the parsed
 *   participant plan; currentTemplateVersion — the live template version the
 *   server derived (never the client claim).
 * Outputs: CompatibleInitialCaseResult — decided only after EVERY
 *   same-program case is scanned. Precedence: any started/terminal/attached
 *   initial → INITIAL_EVENT_ALREADY_STARTED; else any active incompatible
 *   scheduled initial → INCOMPATIBLE_CASE; else the first compatible
 *   untouched scheduled initial → reuse; else `none`. Reuse is never
 *   returned early — corruption/duplicates later in the scan still win.
 * Failure Handling: none — pure scan.
 * Idempotency: REQUIRED — deterministic given the same input ordering.
 */
export function findCompatibleNeverStartedInitialCase(
  cases: unknown[],
  participantIntent: ComplexInitialVisitParticipantIntent,
  currentTemplateVersion: number,
): CompatibleInitialCaseResult {
  const entityKey = idKey(participantIntent.entityId);
  const caseTypeKey = idKey(participantIntent.caseType.lookupId);
  const accountKey = idKey(participantIntent.inspectionAccountId);
  const templateKey = idKey(participantIntent.templateId);

  let blocked: CompatibleInitialCaseResult | null = null;
  let incompatibleTemplateCaseId: unknown = null;
  let reuse: CompatibleInitialCaseResult | null = null;

  for (const raw of cases) {
    const caseDoc = raw as Record<string, any>;
    if (!caseDoc || caseDoc._deleted === true) continue;
    if (idKey(caseDoc.entityId) !== entityKey) continue;
    // Prior finished cycles never participate in reuse or blocking.
    if (
      PRIOR_CYCLE_CASE_STATUSES.has(String(caseDoc.caseStatus)) ||
      caseDoc.archivedAt !== undefined
    ) {
      continue;
    }
    // Case-level program tuple: entity + caseType + account + fiscalYear.
    const sameProgram =
      idKey((caseDoc.caseType as Record<string, any> | undefined)?.lookupId) === caseTypeKey &&
      idKey(caseDoc.inspectionAccountId) === accountKey &&
      caseDoc.fiscalYear === participantIntent.fiscalYear;
    if (!sameProgram) continue;
    const templateMatches =
      idKey(caseDoc.templateId) === templateKey &&
      caseDoc.templateVersion === currentTemplateVersion;
    const events = readEvents(caseDoc);
    for (let index = 0; index < events.length; index += 1) {
      const event = events[index];
      if (!eventIsMatchingInitial(event, participantIntent)) continue;
      const hasVisitObject =
        event.complexVisitManifest !== undefined || event.complexVisitReference !== undefined;
      const neverTouched =
        event.startedAt === undefined &&
        event.completedAt === undefined &&
        event.closedOutAt === undefined;
      if (event.status === 'scheduled' && neverTouched && !hasVisitObject) {
        if (templateMatches) {
          // Remember the FIRST compatible initial — but keep scanning so a
          // later started/attached initial still blocks instead of reusing.
          if (reuse === null) {
            reuse = { kind: 'reuse', caseId: caseDoc._id, eventIndex: index };
          }
        } else if (incompatibleTemplateCaseId === null) {
          // Active scheduled initial on the wrong template policy — a second
          // initial is never created.
          incompatibleTemplateCaseId = caseDoc._id;
        }
        continue;
      }
      if (blocked === null) {
        blocked = { kind: 'blocked', reason: 'INITIAL_EVENT_ALREADY_STARTED', caseId: caseDoc._id };
      }
    }
  }

  if (blocked !== null) return blocked;
  if (incompatibleTemplateCaseId !== null) {
    return { kind: 'blocked', reason: 'INCOMPATIBLE_CASE', caseId: incompatibleTemplateCaseId };
  }
  if (reuse !== null) return reuse;
  return { kind: 'none' };
}

/**
 * @process inspectionResultCandidates
 * Steps: return the ordered candidate list for the stored inspection result —
 *   strict `event.details.inspectionResult` FIRST (schemaVersion:1 complex
 *   target cases store it there), then the legacy event/case top-level
 *   fields. Callers apply their own `inspectionResultIndicatesViolations`
 *   predicate with `.some(...)`.
 * Inputs: event — the completed event; caseDoc — the containing case.
 * Outputs: `[details.inspectionResult, event.inspectionResult, event.result,
 *   case.inspectionResult, case.result]` — undefined entries are kept so
 *   ordering is stable.
 * Failure Handling: none — pure read; missing fields yield undefined.
 * Idempotency: REQUIRED — deterministic pure projection.
 */
export function inspectionResultCandidates(event: unknown, caseDoc: unknown): unknown[] {
  const evt = (event ?? {}) as Record<string, any>;
  const doc = (caseDoc ?? {}) as Record<string, any>;
  const details = evt.details as Record<string, any> | undefined;
  return [
    details?.inspectionResult,
    evt.inspectionResult,
    evt.result,
    doc.inspectionResult,
    doc.result,
  ];
}
